import { useDebouncedValue, useDidUpdate } from '@mantine/hooks';
import { ColumnSizingState, RowData, SortingState } from '@tanstack/react-table';
import { DataGridEmptyViewOptions, DataGridSimple, DataGridSimpleProps } from '@vision/ui/components';
import { DATA_GRID_DEFAULT_CURRENT_PAGE, DATA_GRID_DEFAULT_PER_PAGE } from '@vision/ui/constants';
import { DataGridContextProvider } from '@vision/ui/context/DataGridContext';
import { useDataGridParams } from '@vision/ui/hooks';
import { DataGridFilterChange, Filter, Meta, Nullable, Pagination } from '@vision/ui/interfaces';
import {
  ensureArray,
  normalizeSelectionsWithMetaResponse,
  parseQueryObjectToSelections,
  parseSelectionsToQueryObject,
  processFilterSelections,
} from '@vision/ui/utils';
import qs from 'qs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import { DataGridActionSlot } from './components';

interface DefaultSortParam {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

export interface DataGridProps<TData extends RowData> extends DataGridSimpleProps<TData> {
  extraQueryParams?: Record<string, boolean | number | string>;
  meta: Nullable<Meta>;
  onColumnSizeChange?: (value: ColumnSizingState) => void;
  onFilterChange?: (data: DataGridFilterChange) => void;
  onRecordAdd?: VoidFunction;
  recordAddButtonLabel?: string;
  recordAddButtonVisible?: boolean;
  searchTooltip?: string;
  defaultSortParam?: DefaultSortParam;
  renderContent?: (props: { data: TData[]; meta: Meta; actionSlot: React.ReactNode }) => React.ReactNode;
  onRecordAddPermission?: string;
  filterKey: string;
}

function _DataGrid<TData extends RowData>({
  actionSlot,
  searchTooltip,
  children,
  emptyViewOptions,
  extraQueryParams,
  meta: metaProp,
  onFilterChange,
  onRecordAdd,
  recordAddButtonLabel,
  recordAddButtonVisible = true,
  defaultSortParam,
  renderContent,
  onRecordAddPermission,
  filterKey,
  ...props
}: React.PropsWithChildren<DataGridProps<TData>>) {
  const { i18n } = useTranslation(['translation', 'data-grid']);
  const {
    filterSelectionsDataGridUrlParams,
    dataGridUrlParamsWithFilterSelections,
    query,
    updateDataGridUrlParams,
    params,
  } = useDataGridParams(extraQueryParams);
  const [meta, setMeta] = useState<Meta>(() => metaProp);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [internalPagination, setInternalPagination] = useState<Pagination>(meta?.pagination);
  const skipFilterChange = useRef<boolean>(false);
  const pendingFiltersRef = useRef<{ filter: Filter; key: string }[]>([]);
  const [trigger, setTrigger] = useState<number>(0);
  const [debouncedTrigger] = useDebouncedValue(trigger, 300);

  const handleChangePageSize = useCallback(
    (value: number) => {
      updateDataGridUrlParams({
        page: DATA_GRID_DEFAULT_CURRENT_PAGE, // when page size changes, reset to page: 1
        per_page: value,
      });
    },
    [updateDataGridUrlParams],
  );

  const handleChangePage = useCallback(
    (value: number) => {
      updateDataGridUrlParams({
        page: value,
      });
    },
    [updateDataGridUrlParams],
  );

  const handleChangeColumnSize = useCallback((value: ColumnSizingState) => {
    if (props.onColumnSizeChange) {
      props.onColumnSizeChange(value);
    }
  }, []);

  const currentPage = useMemo(() => {
    if (dataGridUrlParamsWithFilterSelections.page) {
      return Number(dataGridUrlParamsWithFilterSelections.page);
    }
    return meta?.pagination?.current || DATA_GRID_DEFAULT_CURRENT_PAGE;
  }, [dataGridUrlParamsWithFilterSelections, meta]);

  const pageSize = useMemo(() => {
    if (dataGridUrlParamsWithFilterSelections.per_page) {
      return Number(dataGridUrlParamsWithFilterSelections.per_page);
    }
    return meta?.pagination?.per_page || DATA_GRID_DEFAULT_PER_PAGE;
  }, [dataGridUrlParamsWithFilterSelections, meta]);

  const memoizedEmptyViewOptions: DataGridEmptyViewOptions = useMemo(() => {
    return {
      ...emptyViewOptions,
      buttonVisible: emptyViewOptions?.buttonVisible ?? recordAddButtonVisible,
    };
  }, [emptyViewOptions, recordAddButtonVisible]);

  useDidUpdate(() => {
    setInternalPagination({
      ...meta?.pagination,
      current: currentPage,
      per_page: pageSize,
    });
  }, [meta?.pagination, currentPage, pageSize]);

  useEffectOnce(() => {
    // Non-grid query params (for example a locked report source) must not suppress the default sort.
    const queryParams = new URLSearchParams(window.location.search);
    if (defaultSortParam && !queryParams.has('sort_by') && !queryParams.has('sort_order')) {
      updateDataGridUrlParams({
        sort_by: defaultSortParam.sortBy,
        sort_order: defaultSortParam.sortOrder,
      });
    }
  });

  useDidUpdate(() => {
    if (sorting.length) {
      const { desc, id } = sorting[0];

      updateDataGridUrlParams({
        sort_by: id,
        sort_order: desc ? 'desc' : 'asc',
      });
    } else {
      updateDataGridUrlParams({
        sort_by: null,
        sort_order: null,
      });
    }
  }, [sorting]);

  useDidUpdate(() => {
    // Api response u bekle
    if (!meta) {
      return;
    }

    const selectionsFromUrl = normalizeSelectionsWithMetaResponse(
      meta,
      parseQueryObjectToSelections(filterSelectionsDataGridUrlParams),
    );

    // URL de zaten filter query var ise veya filter lar kullanıcı tarafından değiştirildiyse olanı kullan ve override etme
    if (selectionsFromUrl.length || dataGridUrlParamsWithFilterSelections.fc) {
      return;
    }

    const selectionsFromResponse = processFilterSelections(meta);

    if (selectionsFromResponse.length) {
      /*
         1- Eğer sayfa açılışında hiç query param yok ise default olarak page ve per_page ekleniyor
         2- Parent componente onFilterChange event ı ile query string gönderiliyor
         3- Parent component page ve per_page içeren bu query string i kullanarak ilk api isteğini atıyor
         4- Gelen response da eğer default seçili filter var ise bu seçili filter ları URL e query param olarak ekliyoruz
         5- Ekledikten sonra ikinci bir istek atmamak için bu kontrol mevcut çünkü zaten ilk response da backend default filter seçimlerine göre veri dönüyor olacak
      */
      skipFilterChange.current = true;
      updateDataGridUrlParams({
        extraQueryValues: parseSelectionsToQueryObject(selectionsFromResponse),
      });
    }
  }, [meta]);

  useDidUpdate(() => {
    if (skipFilterChange.current) {
      skipFilterChange.current = false;
      return;
    }

    if (query) {
      if (dataGridUrlParamsWithFilterSelections.sort_by) {
        setSorting([
          {
            id: dataGridUrlParamsWithFilterSelections.sort_by,
            desc: dataGridUrlParamsWithFilterSelections.sort_order === 'desc',
          },
        ]);
      }
      onFilterChange?.({
        params,
        query,
        queryWithoutPaginationAndSorting: qs.stringify(params.extraQueryValues),
      });
    }

    // Dil değiştiğinde tekrar api isteği atılması gerek, api responseda bazı label lar translate ediliyor
  }, [query, i18n.language]);

  useDidUpdate(() => {
    if (!metaProp) {
      return;
    }

    // Elimizde dynamic filter'lar için options var ise gelen meta ile merge etmemiz gerek
    const mergedMeta: Meta = {
      ...metaProp,
      filters: metaProp.filters.map((item) => {
        const found = meta?.filters?.find(({ dynamic, key }) => dynamic && key === item.key);
        if (found) {
          return {
            ...item,
            options: ensureArray(found?.options).length ? found.options : item.options,
          };
        }

        return item;
      }),
    };

    setMeta(mergedMeta);
  }, [metaProp, i18n.language]);

  useDidUpdate(() => {
    if (!metaProp) {
      return;
    }
    /*
     * Eğer meta.pagination.page değeri ile query parameter "page" değeri farklı ise
     * query parameter değerini meta.pagination.page değeri yap
     * */
    const queryParams = new URLSearchParams(window.location.search);
    const page = Number(queryParams.get('page'));
    if (metaProp.pagination.current !== page) {
      skipFilterChange.current = true;
      handleChangePage(metaProp.pagination.current);
    } else {
      skipFilterChange.current = false;
    }
  }, [metaProp]);

  useEffect(() => {
    if (pendingFiltersRef.current.length > 0) {
      const updatedFilters = [...meta.filters];

      pendingFiltersRef.current.forEach(({ filter }) => {
        const index = updatedFilters.findIndex((item) => item.key === filter.key);
        if (index !== -1) {
          updatedFilters[index] = filter;
        } else {
          updatedFilters.push(filter);
        }
      });

      const updatedMeta: Meta = {
        ...meta,
        filters: updatedFilters,
      };

      setMeta(updatedMeta);
      pendingFiltersRef.current = [];
    }
  }, [debouncedTrigger, meta]);

  const handleUpdateFilter = useCallback((filter: Filter, key: string) => {
    pendingFiltersRef.current.push({ filter, key });
    setTrigger((prev) => prev + 1);
  }, []);

  const handleApplyFilter = useCallback(
    (key: string, value: string) => {
      updateDataGridUrlParams({
        extraQueryValues: { ...params.extraQueryValues, [key]: value },
        fc: true,
        page: DATA_GRID_DEFAULT_CURRENT_PAGE,
        per_page: params.per_page ?? DATA_GRID_DEFAULT_PER_PAGE,
      });
    },
    [params.extraQueryValues, params.per_page, updateDataGridUrlParams],
  );

  const dataGridActionSlot = (
    <DataGridActionSlot
      actions={actionSlot}
      searchTooltip={searchTooltip}
      dataGridUrlParamsWithFilterSelections={dataGridUrlParamsWithFilterSelections}
      filterSelectionsDataGridUrlParams={filterSelectionsDataGridUrlParams}
      meta={meta}
      onRecordAdd={onRecordAdd}
      onRecordAddPermission={onRecordAddPermission}
      params={params}
      recordAddButtonLabel={recordAddButtonLabel}
      recordAddButtonVisible={recordAddButtonVisible}
      updateDataGridUrlParams={updateDataGridUrlParams}
      filterKey={filterKey}
    >
      {children}
    </DataGridActionSlot>
  );

  const memoizedDataGridContextValues = useMemo(() => {
    return {
      meta,
      applyFilter: handleApplyFilter,
      updateFilter: handleUpdateFilter,
    };
  }, [meta, handleApplyFilter, handleUpdateFilter]);

  return (
    <DataGridContextProvider value={memoizedDataGridContextValues}>
      {renderContent ? (
        renderContent({
          data: props.data as TData[],
          meta,
          actionSlot: dataGridActionSlot,
        })
      ) : (
        <DataGridSimple
          pagination={internalPagination}
          sortingState={sorting}
          onPageChange={handleChangePage}
          onPageSizeChange={handleChangePageSize}
          onColumnSizeChange={handleChangeColumnSize}
          onSortChange={setSorting}
          actionSlot={dataGridActionSlot}
          withFilters={props.withFilters ?? true}
          emptyViewOptions={memoizedEmptyViewOptions}
          {...props}
        />
      )}
    </DataGridContextProvider>
  );
}

export const DataGrid = React.memo(_DataGrid) as typeof _DataGrid;
